import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import VideoCard from '@/components/feature/VideoCard';
import FilterBar from '@/pages/category/components/FilterBar';
import { getVideosByCategory } from '@/lib/videoData';
import { getCategoryMeta, type VideoCategory } from '@/types/video';
import { useSeo } from '@/hooks/useSeo';

const PAGE_SIZE = 8;
const VALID: VideoCategory[] = ['movie', 'tv', 'variety'];

export default function Category() {
  const { type = '' } = useParams();
  const meta = getCategoryMeta(type);
  const isValid = VALID.includes(type as VideoCategory);

  const [region, setRegion] = useState('全部');
  const [year, setYear] = useState('全部');
  const [sort, setSort] = useState<'update' | 'hot'>('update');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const list = useMemo(
    () =>
      isValid
        ? getVideosByCategory(type as VideoCategory, { region, year, sort })
        : [],
    [type, region, year, sort, isValid]
  );

  useSeo({
    title: `${meta?.name ?? '分类'} - 桃子视频`,
    description: `浏览桃子视频中丰富的${meta?.name ?? ''}高清视频，包括最新电影、热门电视剧等内容。`,
    keywords: `桃子视频,${meta?.name ?? ''},高清视频,在线观看`,
  });

  useEffect(() => {
    setPage(1);
  }, [type, region, year, sort]);

  // 切换筛选时模拟加载过程，提升真实感
  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 260);
    return () => clearTimeout(timer);
  }, [type, region, year, sort, page]);

  const pageItems = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetAll = () => {
    setRegion('全部');
    setYear('全部');
    setSort('update');
  };

  if (!isValid) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <main className="mx-auto flex max-w-7xl flex-col items-center justify-center px-4 py-24 text-center md:px-8">
          <i className="ri-error-warning-line text-5xl text-foreground-600" />
          <h1 className="mt-4 text-xl font-semibold text-foreground-50">分类不存在</h1>
          <p className="mt-2 text-sm text-foreground-400">没有找到该分类，请从首页重新选择。</p>
          <Link
            to="/"
            className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-950">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 py-6 md:px-8 md:py-8">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '分类' }, { label: meta?.name ?? '' }]} />

        <header className="mt-5 flex items-start gap-4 rounded-lg border border-background-800/80 bg-gradient-to-r from-primary-900/30 to-background-900 p-5 md:p-6">
          <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className={`${meta?.icon} text-3xl`} />
          </span>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">
              {meta?.name}频道
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-300">
              {meta?.intro}
            </p>
          </div>
        </header>

        <div className="mt-6">
          <FilterBar
            region={region}
            year={year}
            sort={sort}
            resultCount={list.length}
            onRegion={setRegion}
            onYear={setYear}
            onSort={setSort}
          />
        </div>

        <section className="mt-6">
          {loading ? (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="animate-pulse rounded-lg border border-background-800/70 bg-background-900">
                  <div className="aspect-[3/4] w-full rounded-t-lg bg-background-800" />
                  <div className="space-y-2 p-3">
                    <div className="h-4 w-3/4 rounded bg-background-800" />
                    <div className="h-3 w-1/2 rounded bg-background-800" />
                  </div>
                </div>
              ))}
            </div>
          ) : pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                {pageItems.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
              <Pagination
                total={list.length}
                current={page}
                pageSize={PAGE_SIZE}
                onChange={(p) => {
                  setPage(p);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-700 bg-background-900/40 py-20 text-center">
              <i className="ri-emotion-sad-line text-5xl text-foreground-600" />
              <h3 className="mt-4 text-base font-semibold text-foreground-100">
                暂无相关视频，试试其它筛选条件
              </h3>
              <p className="mt-2 text-sm text-foreground-400">
                当前筛选条件下没有匹配的内容，你可以重置筛选或浏览其他分类。
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={resetAll}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line" />
                  重置筛选
                </button>
                <Link
                  to="/"
                  className="whitespace-nowrap rounded-md border border-background-600 px-5 py-2.5 text-sm font-medium text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-400"
                >
                  返回首页
                </Link>
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}